// power.ts - what one solar panel makes, in one copy both sides run.
//
// The Atlas measures a cell's insolation off the DTM and the server runs the
// grid off it, so the arithmetic cannot live twice: `src/atlas/yield.ts`'s
// kWPerPanelAt calls straight through here, and `server/engine/types.ts`
// reaches the same expression with the inputs a STRUCT# row recorded when the
// array went up.

/** The habitat's own reserve, kW: added to every crew's production and never
 * removable, so demolishing every array can never strand a crew at zero. */
export const HABITAT_BACKUP_KW = 0.6;

/** Collector area of one panel, m2. */
export const PANEL_M2 = 20;

/**
 * Net cell-to-grid efficiency. Invented, like the panel area: the insolation
 * these multiply is what is measured.
 */
export const PV_NET_EFFICIENCY = 0.19;

/**
 * Bounded by half Mercury's own peak, not the whole of it: 1361 W/m2 at 1 AU
 * over its 0.3075 AU perihelion is 14,394, halved to 7,197 since half of
 * every sol there is night; Earth and Mars keep a polar day and stay under it.
 */
export const MAX_MEAN_FLUX_W_M2 = 7_200;

/**
 * Dust thick enough to stop a panel outright. A planet-encircling storm
 * exceeds tau 4 and no body row's seasonal curve goes near it, so this bounds
 * a stored figure rather than describing weather.
 */
export const MAX_DUST_TAU = 20;

/** What a cell's own sunlight averages over a sol, W/m2. */
export function meanFluxWm2(insolationWhSol: number, solLengthS: number): number {
  return insolationWhSol / (solLengthS / 3600);
}

/**
 * The inputs one placed array's output is computed from, recorded on the row
 * when it went up.
 *
 * The dust reading is frozen at the season of the build on purpose: the
 * server holds no season clock, and pricing a standing array against a Ls it
 * cannot see would be an invented number rather than a stale one.
 */
export interface PanelSite {
  insolationWhSol: number;
  solLengthS: number;
  dustTau: number;
}

/** Sol-average kW from one panel on this ground. */
export function kWPerPanelFrom(site: PanelSite): number {
  return PANEL_M2 * meanFluxWm2(site.insolationWhSol, site.solLengthS)
    * PV_NET_EFFICIENCY * Math.exp(-0.4 * site.dustTau) / 1000;
}

/**
 * Whether a site is a reading of real ground rather than a number a request
 * chose. Checked at the door that writes the row, so the grid can sum what it
 * finds without re-deciding.
 */
export function isUsablePanelSite(site: PanelSite): boolean {
  const { insolationWhSol, solLengthS, dustTau } = site;
  if (!Number.isFinite(insolationWhSol) || insolationWhSol <= 0) return false;
  if (!Number.isFinite(solLengthS) || solLengthS <= 0) return false;
  if (!Number.isFinite(dustTau) || dustTau < 0 || dustTau > MAX_DUST_TAU) return false;
  return meanFluxWm2(insolationWhSol, solLengthS) <= MAX_MEAN_FLUX_W_M2;
}
